import { Box } from "@chakra-ui/react";
import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

interface FeedFullscreenChromeProps {
  onClose: () => void;
  children: ReactNode;
  // Optional horizontal-swipe hook. comic_release fullscreen uses this to
  // dispatch the feed-page-nav event. Comment fullscreen leaves it
  // undefined since horizontal swipes don't mean anything there.
  onHorizontalSwipe?: (deltaX: number) => void;
}

// Pinch-in close threshold: treat as "pinched closed" when current spread is
// below 70% of the initial spread between the two pointers.
const PINCH_CLOSE_RATIO = 0.7;
// Swipe close: a single-finger drag exceeding EITHER the distance OR the
// velocity gate (vertical-dominant) dismisses. Two gates so a long-but-slow
// drag and a short-but-fast flick both feel intentional.
const SWIPE_CLOSE_DISTANCE_PX = 100;
const SWIPE_CLOSE_VELOCITY = 0.35;
// Drag-intent: any movement past this on a single pointer marks
// window.__feedJustDragged so an underlying click handler doesn't fire
// after the gesture ends. Without this a swipe-that-doesn't-quite-meet-
// dismiss falls through to whatever the inner content's click does.
const DRAG_INTENT_PX = 8;
// Horizontal swipe threshold — only used when onHorizontalSwipe is set.
// Mirrors SuggestionFeed's HSWIPE_THRESHOLD so the feel matches.
const HORIZONTAL_SWIPE_PX = 50;

/**
 * Shared chrome for the suggestion feed's fullscreen presentations
 * (FeedFullscreenPreview, FeedFullscreenComment). Provides:
 *
 *   - Portal mount at document.body so we sit above the feed scroll
 *     container regardless of where we're rendered in the tree.
 *   - Black backdrop, fixed inset, high z-index.
 *   - Pinch-in dismiss (current spread < 70% of initial).
 *   - Vertical-dominant swipe dismiss (distance OR velocity gate).
 *   - ESC key dismiss.
 *   - Close button in the top-right corner with safe-area padding,
 *     above the deck's stacking context.
 *   - __feedJustDragged hand-off so click-based zone nav inside the
 *     children's tree is suppressed once a drag begins.
 *
 * Children render inside a touchAction:none gesture-tracking layer that
 * sits below the close button on the stacking order.
 */
export function FeedFullscreenChrome({
  onClose,
  children,
  onHorizontalSwipe,
}: FeedFullscreenChromeProps) {
  // ESC dismisses.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Pointer-based gesture detection. Up to two active pointers tracked;
  // pair = pinch, single = swipe.
  const pointers = useRef<Map<number, { x: number; y: number; t: number }>>(
    new Map(),
  );
  const initialSpread = useRef<number | null>(null);
  const swipeStart = useRef<{ x: number; y: number; t: number } | null>(null);

  const distance = useCallback(
    (a: { x: number; y: number }, b: { x: number; y: number }) =>
      Math.hypot(a.x - b.x, a.y - b.y),
    [],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, a, [role="button"]')) {
        return;
      }
      pointers.current.set(e.pointerId, {
        x: e.clientX,
        y: e.clientY,
        t: e.timeStamp,
      });
      const ptArr = Array.from(pointers.current.values());
      if (ptArr.length === 2) {
        initialSpread.current = distance(ptArr[0], ptArr[1]);
        swipeStart.current = null;
      } else if (ptArr.length === 1) {
        swipeStart.current = { x: e.clientX, y: e.clientY, t: e.timeStamp };
      }
    },
    [distance],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const stored = pointers.current.get(e.pointerId);
      if (!stored) return;
      pointers.current.set(e.pointerId, {
        x: e.clientX,
        y: e.clientY,
        t: e.timeStamp,
      });

      // Drag-intent flag — once any single-finger movement clears the
      // small drag threshold, mark the global drag flag so click-based
      // nav in the children's tree skips after this gesture.
      const start = swipeStart.current;
      if (start) {
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (Math.hypot(dx, dy) > DRAG_INTENT_PX) {
          (window as { __feedJustDragged?: number }).__feedJustDragged =
            Date.now();
        }
      }

      const ptArr = Array.from(pointers.current.values());
      if (ptArr.length === 2 && initialSpread.current !== null) {
        const spread = distance(ptArr[0], ptArr[1]);
        if (spread / initialSpread.current < PINCH_CLOSE_RATIO) {
          pointers.current.clear();
          initialSpread.current = null;
          onClose();
        }
      }
    },
    [distance, onClose],
  );

  const finishPointer = useCallback(
    (e: React.PointerEvent) => {
      const stored = pointers.current.get(e.pointerId);
      pointers.current.delete(e.pointerId);

      if (pointers.current.size < 2) {
        initialSpread.current = null;
      }

      const start = swipeStart.current;
      if (start && stored && pointers.current.size === 0) {
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        const dt = Math.max(1, e.timeStamp - start.t);
        const distancePx = Math.hypot(dx, dy);
        const velocity = distancePx / dt;
        const verticalDominant = Math.abs(dy) > Math.abs(dx);
        if (verticalDominant) {
          const distanceTriggered = Math.abs(dy) >= SWIPE_CLOSE_DISTANCE_PX;
          const velocityTriggered =
            velocity >= SWIPE_CLOSE_VELOCITY && Math.abs(dy) >= 60;
          if (distanceTriggered || velocityTriggered) {
            swipeStart.current = null;
            onClose();
            return;
          }
        } else if (
          onHorizontalSwipe &&
          Math.abs(dx) >= HORIZONTAL_SWIPE_PX
        ) {
          swipeStart.current = null;
          onHorizontalSwipe(dx);
          return;
        }
      }
      if (pointers.current.size === 0) {
        swipeStart.current = null;
      }
    },
    [onClose, onHorizontalSwipe],
  );

  const portalRoot = useMemo(
    () => (typeof document !== "undefined" ? document.body : null),
    [],
  );

  if (!portalRoot) return null;

  const content = (
    <Box position="fixed" inset={0} bg="black" zIndex={2000}>
      <Box
        position="absolute"
        inset={0}
        display="flex"
        flexDirection="column"
        sx={{ touchAction: "none", overscrollBehavior: "contain" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
      >
        {children}
      </Box>

      {/* Close button — own stacking layer above the gesture tracker AND
          any inner stacked content (e.g. FeedPagesViewer's deck at z=500).
          Wrapper is pointer-events:none so taps elsewhere reach the
          gesture tracker; the button itself opts back in. */}
      <Box
        position="absolute"
        top={0}
        right={0}
        zIndex={1000}
        pt="max(env(safe-area-inset-top, 8px), 8px)"
        pr="max(env(safe-area-inset-right, 8px), 8px)"
        pointerEvents="none"
      >
        <Box
          as="button"
          type="button"
          aria-label="Close preview"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
          }}
          w="48px"
          h="48px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          bg="rgba(0,0,0,0.6)"
          border="1px solid rgba(255,255,255,0.22)"
          color="white"
          fontSize="22px"
          fontWeight="bold"
          lineHeight="1"
          cursor="pointer"
          pointerEvents="auto"
          sx={{
            backdropFilter: "blur(8px)",
            touchAction: "auto",
          }}
          _hover={{ bg: "rgba(0,0,0,0.8)" }}
          _active={{ transform: "scale(0.96)" }}
        >
          <Box as="span" aria-hidden>×</Box>
        </Box>
      </Box>
    </Box>
  );

  return createPortal(content, portalRoot);
}
